import {
  MAX_DICE_SLOTS,
  STARTING_UNLOCKED_DICE_SLOTS,
  type DieFace,
  type DieState,
  type DiceTrayState,
} from './types';
import type { SeededRng } from './rng';

function clampUnlockedCount(count: number): number {
  if (!Number.isInteger(count)) {
    throw new Error(`unlockedDiceSlots must be an integer, got ${count}`);
  }
  if (count < 1 || count > MAX_DICE_SLOTS) {
    throw new Error(
      `unlockedDiceSlots must be between 1 and ${MAX_DICE_SLOTS}, got ${count}`,
    );
  }
  return count;
}

export function createEmptyDie(id: number, unlocked: boolean): DieState {
  return {
    id,
    unlocked,
    face: undefined,
    held: false,
    primarySkillId: null,
    primarySkillLevel: 0,
  };
}

/**
 * Builds a six-slot tray. Only the first `unlockedCount` slots are unlocked.
 * Locked dice must never be rolled, rendered as rollable, or included in combos.
 */
export function createDiceTray(
  unlockedCount: number = STARTING_UNLOCKED_DICE_SLOTS,
): DiceTrayState {
  const count = clampUnlockedCount(unlockedCount);
  const dice: DieState[] = [];
  for (let id = 0; id < MAX_DICE_SLOTS; id += 1) {
    dice.push(createEmptyDie(id, id < count));
  }
  return { dice, unlockedCount: count };
}

export function getUnlockedDice(tray: DiceTrayState): DieState[] {
  return tray.dice.filter((die) => die.unlocked);
}

export function getLockedDice(tray: DiceTrayState): DieState[] {
  return tray.dice.filter((die) => !die.unlocked);
}

function rollFace(rng: SeededRng): DieFace {
  return rng.nextInt(1, 6) as DieFace;
}

/**
 * Rolls every unlocked die. Locked dice are never touched.
 */
export function rollUnlockedDice(
  tray: DiceTrayState,
  rng: SeededRng,
): DiceTrayState {
  const dice = tray.dice.map((die) => {
    if (!die.unlocked) {
      return { ...die };
    }
    return {
      ...die,
      face: rollFace(rng),
      held: false,
    };
  });
  return { dice, unlockedCount: tray.unlockedCount };
}

/**
 * Rerolls unlocked, unheld dice. Locked and held dice keep their faces.
 * Combat reroll budgets are enforced by callers (later milestones).
 */
export function rerollUnheldDice(
  tray: DiceTrayState,
  rng: SeededRng,
): DiceTrayState {
  const dice = tray.dice.map((die) => {
    if (!die.unlocked || die.held) {
      return { ...die };
    }
    return {
      ...die,
      face: rollFace(rng),
    };
  });
  return { dice, unlockedCount: tray.unlockedCount };
}

/**
 * Rerolls a single unlocked die (even if held). Locked dice cannot be targeted.
 */
export function rerollSingleDie(
  tray: DiceTrayState,
  dieId: number,
  rng: SeededRng,
): DiceTrayState {
  const target = tray.dice.find((die) => die.id === dieId);
  if (!target) {
    throw new Error(`Unknown die id: ${dieId}`);
  }
  if (!target.unlocked) {
    throw new Error(`Cannot reroll locked die ${dieId}`);
  }

  const dice = tray.dice.map((die) => {
    if (die.id !== dieId) {
      return { ...die };
    }
    return {
      ...die,
      face: rollFace(rng),
    };
  });
  return { dice, unlockedCount: tray.unlockedCount };
}

/**
 * Clears faces/holds for a new turn while preserving unlock and skill assignment.
 */
export function clearDiceFaces(tray: DiceTrayState): DiceTrayState {
  return {
    unlockedCount: tray.unlockedCount,
    dice: tray.dice.map((die) => ({
      ...die,
      face: undefined,
      held: false,
    })),
  };
}

/**
 * Toggles hold on an unlocked die that already has a face.
 * Locked dice and unrolled dice cannot be held.
 */
export function toggleDieHold(
  tray: DiceTrayState,
  dieId: number,
): DiceTrayState {
  const target = tray.dice.find((die) => die.id === dieId);
  if (!target) {
    throw new Error(`Unknown die id: ${dieId}`);
  }
  if (!target.unlocked) {
    throw new Error(`Cannot hold locked die ${dieId}`);
  }
  if (target.face === undefined) {
    throw new Error(`Cannot hold unrolled die ${dieId}`);
  }

  return {
    unlockedCount: tray.unlockedCount,
    dice: tray.dice.map((die) =>
      die.id === dieId ? { ...die, held: !die.held } : { ...die },
    ),
  };
}

/** Sets hold explicitly (used by tests and UI sync). */
export function setDieHeld(
  tray: DiceTrayState,
  dieId: number,
  held: boolean,
): DiceTrayState {
  const target = tray.dice.find((die) => die.id === dieId);
  if (!target) {
    throw new Error(`Unknown die id: ${dieId}`);
  }
  if (!target.unlocked) {
    throw new Error(`Cannot hold locked die ${dieId}`);
  }
  if (target.face === undefined) {
    throw new Error(`Cannot hold unrolled die ${dieId}`);
  }

  return {
    unlockedCount: tray.unlockedCount,
    dice: tray.dice.map((die) =>
      die.id === dieId ? { ...die, held } : { ...die },
    ),
  };
}
